/**
 * Site chrome shared by the landing page and the legal placeholders: the header nav and the
 * footer. Product links point at landing sections that exist. Privacy and terms are honest
 * placeholders, not invented policies. Support is live at /support. Practitioners sign in from
 * the footer (ONB-005).
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { focusRing } from "@lattice/ui/focus";
import { Mark } from "@lattice/ui/Mark";
import { usePath } from "./router.ts";
import { useUploads } from "./Upload.tsx";
const TWO_UP = "@media (min-width: 720px)";

/*
  The header's links sit in a row from here. The brand, four links and the upload button need about
  740px with their gaps, so at 720px, which is a 1440px screen at 200% zoom, the row wrapped onto two
  lines. Below this the links are in the Menu.
*/
const LINKS_IN_ROW = "@media (min-width: 800px)";
const GUTTER = "clamp(20px, 4vw, 56px)";
const NAV_LINKS = [{
  href: "/#how",
  label: "How it works"
}, {
  href: "/#questions",
  label: "Your choices"
}, {
  href: "/#pricing",
  label: "Pricing"
}, {
  href: "/compare",
  label: "Comparisons"
}] as const;
const PRODUCT_LINKS = [...NAV_LINKS, {
  href: "/account",
  label: "Account"
}, {
  href: "/practitioners",
  label: "Practitioner sign-in",
  testId: "practitioner-sign-in"
}] as const;
const LEGAL_LINKS = [{
  href: "/privacy",
  label: "Privacy"
}, {
  href: "/terms",
  label: "Terms"
}, {
  href: "/support",
  label: "Support"
}] as const;
const styles = {
  brand: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1173whf",
    kAzted: "x1envrbd",
    kMwMTN: "xgjxun8",
    kybGjl: "x1hl2dhg",
    k63SB2: "x117lpx5",
    kGuDYH: "x11mdwsw",
    kSiTet: "x1hc1fzr x5z6fxw",
    kaIpWk: "x8wwnw1",
    kjBf7l: "x2jzzdg",
    kInvED: "x1ewu8gn",
    kMeerF: "xzk9mvr",
    k3XXqK: "x1t137rt x9v5kkp",
    $$css: true
  },
  footerBrand: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1173whf",
    kAzted: "x1envrbd",
    kMwMTN: "xzb3lse",
    kybGjl: "x1hl2dhg",
    k63SB2: "x117lpx5",
    kGuDYH: "x11mdwsw",
    kSiTet: "x1hc1fzr x5z6fxw",
    kaIpWk: "x8wwnw1",
    kSGwAc: "xqcrz7y",
    $$css: true
  }
};
function currentNav(path: string, href: string): boolean {
  if (href === "/compare") return path === "/compare" || path.startsWith("/compare/");
  return false;
}
export function SiteHeader() {
  const path = usePath();
  const {
    choose
  } = useUploads();
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  /* The links move into the bar from this width, so a panel left open has nothing to be beside. */
  useEffect(() => {
    const row = window.matchMedia(LINKS_IN_ROW.replace("@media ", ""));
    const closeInRow = () => {
      if (row.matches) setOpen(false);
    };
    row.addEventListener("change", closeInRow);
    return () => row.removeEventListener("change", closeInRow);
  }, []);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  return <header>
      <div className="x78zum5 x6s0dn4 x1qughib x1r0x45p x8nclml xp1t01x">
        <a href="/" {...stylex.props(styles.brand, focusRing)} aria-label="Lattice Nutrition home">
          <Mark size={24} />
          <span className="xzb3lse">Lattice</span>
        </a>
        <nav className="x1s85apg x1i24jy5 x6s0dn4 x15agyyk x9hf84i" aria-label="Main">
          {NAV_LINKS.map(link => <a key={link.href} href={link.href} aria-current={currentNav(path, link.href) ? "page" : undefined} {...{
          0: {
            className: "x3nfvp2 x6s0dn4 x1envrbd x1jty25b x1hl2dhg xt0b8zv x3pvw3i x8wwnw1 x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp"
          },
          1: {
            className: "x3nfvp2 x6s0dn4 x1envrbd x1hl2dhg xt0b8zv x3pvw3i x8wwnw1 x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp xzb3lse xqu410z"
          }
        }[!!currentNav(path, link.href) << 0]}>
              {link.label}
            </a>)}
        </nav>
        <div className="x78zum5 x6s0dn4 x1173whf xvc5jky">
          <button type="button" className="x3nfvp2 x6s0dn4 x1envrbd xerji9v xc342km xzb3lse x1cikjda xl4dl4e xqu410z x1ypdohk x1m3fsnl x1890um5 xe7fqdy x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp" onClick={choose}>
            Upload your labs
          </button>
          <button ref={menuButton} type="button" aria-expanded={open} aria-controls="site-menu" className="x3nfvp2 xm7dv08 x6s0dn4 xl56j7k x1p8lnip x1envrbd xerji9v xc342km xjbqb8w xzb3lse x1cikjda xl4dl4e xqu410z x1hl2dhg xt0b8zv x1ypdohk x1m3fsnl x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp" onClick={() => setOpen(value => !value)}>
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>
      {open ? <nav id="site-menu" className="x78zum5 xdt5ytf x1173whf x42188t" aria-label="Site">
          {/* The phone has no other way to Account short of the footer, so the menu lists it. */}
          {PRODUCT_LINKS.map(link => <a key={link.href} href={link.href} onClick={() => setOpen(false)} aria-current={currentNav(path, link.href) ? "page" : undefined} {...{
        0: {
          className: "x3nfvp2 x6s0dn4 x1envrbd x1jty25b x1hl2dhg xt0b8zv x3pvw3i x8wwnw1 x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 x1envrbd x1hl2dhg xt0b8zv x3pvw3i x8wwnw1 x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp xzb3lse xqu410z"
        }
      }[!!currentNav(path, link.href) << 0]}>
              {link.label}
            </a>)}
        </nav> : null}
    </header>;
}
function LinkColumn({
  title,
  labelledBy,
  links
}: {
  readonly title: string;
  readonly labelledBy: string;
  readonly links: readonly {
    href: string;
    label: string;
    testId?: string;
  }[];
}) {
  return <nav className="x78zum5 xdt5ytf x1173whf" aria-labelledby={labelledBy}>
      <h2 id={labelledBy} className="x1ghz6dp xl4dl4e xqu410z xzb3lse">
        {title}
      </h2>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
        {links.map(link => <li key={link.href}>
            <a href={link.href} data-testid={link.testId} className="xuxw1ft x3nfvp2 x6s0dn4 x1envrbd x1jty25b x1hl2dhg xt0b8zv x3pvw3i x8wwnw1 x2jzzdg x1ewu8gn xzk9mvr x1t137rt x9v5kkp">
              {link.label}
            </a>
          </li>)}
      </ul>
    </nav>;
}
export function SiteFooter() {
  return <footer className="xez8t42 x13fuv20 x153ygsw">
      <div className="x18zekx4 xvueqy4 xelt9ym xwlyjvd x129n6ie x1b4r2p0 x78zum5 xdt5ytf xd3upel">
        <div className="xrvj5dj x1y6fwsi xs7aycb x12ut5df x1ewte8j x7a106z">
          <div className="x78zum5 xdt5ytf x1r0x45p xuv54rz">
            <a href="/" {...stylex.props(styles.footerBrand, focusRing)} aria-label="Lattice Nutrition home">
              <Mark size={24} />
              Lattice
            </a>
            <p className="x1ghz6dp x1jty25b x1fzhlzt">Physician-signed powder, built from your labs.</p>
          </div>
          <LinkColumn title="Product" labelledBy="footer-product" links={PRODUCT_LINKS} />
          <LinkColumn title="Legal" labelledBy="footer-legal" links={LEGAL_LINKS} />
        </div>
        <div className="x78zum5 xdt5ytf x1r0x45p xjh1bw0 xez8t42 x13fuv20 x153ygsw xl4dl4e x1jty25b">
          <p className="x1ghz6dp xf2d64e x1fzhlzt" data-testid="fda-disclaimer">
            These statements have not been evaluated by the Food and Drug Administration. This
            product is not intended to diagnose, treat, cure or prevent any disease.
          </p>
          <p className="x1ghz6dp xf2d64e x1fzhlzt">© 2026 Lattice Nutrition</p>
        </div>
      </div>
    </footer>;
}